"use client";

import { useEffect, useRef } from "react";
import { getCookie } from "@/utils/cookiesUtils";
import { dntCookieName } from "./constants";
import { loadSynerise } from "./load-synerise";
import { useDntCookie } from "./use-dnt-cookie";
import { useSrTrackingConsent } from "./use-sr-tracking-consent";

// Keep the reload short enough to feel responsive but long enough to
// let the freshly written cookie flush and any in-flight tracking calls settle.
const RELOAD_DELAY_MS = 500;

export function SyneriseScript() {
  const trackingConsent = useSrTrackingConsent();
  const [, setDntCookie] = useDntCookie();
  const hasReloadedRef = useRef(false);

  useEffect(() => {
    if (trackingConsent === null) return;

    const nextDnt = !trackingConsent;
    const previousRaw = getCookie(dntCookieName);
    const cookieWasMissing = previousRaw !== "true" && previousRaw !== "false";
    const previousDnt = cookieWasMissing ? null : previousRaw === "true";

    // First visit: no _snrs_dnt yet. The user just answered CMP, so we persist
    // the derived DNT value and boot Synerise with a fresh cookie - no reload.
    if (previousDnt === null) {
      setDntCookie(nextDnt);
      loadSynerise();
      return;
    }

    // Return visit whose existing cookie agrees with the current CMP answer.
    // Refresh the TTL so the cookie keeps pace with the user's continued
    // consent and boot Synerise normally - still no reload.
    if (previousDnt === nextDnt) {
      setDntCookie(nextDnt);
      loadSynerise();
      return;
    }

    // The user actually flipped their consent (either mid-session after the
    // SDK already booted, or the stored cookie drifted out of sync with CMP).
    // Synerise reads _snrs_dnt only once on SR.init and caches the value in
    // memory, so we persist the new value and reload so the SDK picks up the
    // fresh DNT state on its next boot.
    setDntCookie(nextDnt);
    if (!hasReloadedRef.current) {
      hasReloadedRef.current = true;
      setTimeout(() => {
        window.location.reload();
      }, RELOAD_DELAY_MS);
    }
  }, [trackingConsent, setDntCookie]);

  return null;
}
